import { TicketStatusEnum } from 'abi-compliance-common-types';
import { extractEmployeeDetailsFromAnswers } from '~/entities/TicketDetails/utils/extractEmployeeDetailsFromAnswers';
import { ApiTicket, Ticket } from '../types';
import { analyzeFormFromTicket } from '../utils';
import { detectAndMapDynamicHoverData } from './dynamic';
import {
  generateCoiDescription,
  mapCoiSpecificData,
} from './specific/coiMapper';
import {
  generateGndDescription,
  mapGndSpecificData,
} from './specific/gndMapper';

export function mapApiStatusToUIStatus(
  apiStatus: string
): 'inProgress' | 'notStarted' | 'published' {
  switch (apiStatus) {
    case TicketStatusEnum.NEW:
    case TicketStatusEnum.IN_REVIEW_PENDING_USER:
    case TicketStatusEnum.IN_REVIEW_PENDING_COMPLIANCE_OFFICER:
      return 'inProgress';
    case TicketStatusEnum.PENDING_APPROVALS:
    case TicketStatusEnum.PENDING_APPROVALS_DAG:
      return 'published';
    case TicketStatusEnum.APPROVED:
    case TicketStatusEnum.APPROVED_PENDING_SAP_DOCUMENT:
      return 'published';
    case TicketStatusEnum.NOT_APPROVED:
    case TicketStatusEnum.CANCELLED:
    default:
      return 'notStarted';
  }
}

export function formatApiDateToUI(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return isoDate;
  }
}

function generateDescription(
  apiTicket: ApiTicket,
  formType:
    | 'conflict-of-interest'
    | 'gifts-and-donations'
    | 'other-compliance-matters'
    | 'employee-movement'
    | 'unknown',
  category?: any
): string {
  if (formType === 'conflict-of-interest') {
    return generateCoiDescription(apiTicket);
  }

  if (formType === 'gifts-and-donations' && category) {
    return generateGndDescription(apiTicket, category);
  }

  if (apiTicket.approvalComments?.trim()) {
    return apiTicket.approvalComments;
  }

  const formTypeName = apiTicket.formType.name || 'Compliance matter';
  const zone = apiTicket.requestedBy.zone?.name || 'unknown zone';
  return `${formTypeName} declared by employee in the ${zone}.`;
}

export function apiTicketToUITicket(apiTicket: ApiTicket): Ticket {
  const analysis = analyzeFormFromTicket(apiTicket);
  const { formType, category, subcategory } = analysis;

  let specificData;
  if (formType === 'conflict-of-interest') {
    specificData = mapCoiSpecificData(apiTicket);
  } else if (formType === 'gifts-and-donations' && category) {
    specificData = mapGndSpecificData(apiTicket, category as any);
  }

  const {
    requestedBy,
    inBehalfOfId,
    inBehalfOf,
    formType: apiFormType,
    numericId,
    status,
    createdAt,
    id,
  } = apiTicket;

  const employeeData = inBehalfOf || requestedBy;
  const {
    name: requestedByName,
    employeeId,
    employeeGlobalId,
    employeeBand,
    managerLevel1Name,
    position,
    businessUnit,
    zone,
  } = employeeData;

  const answersForExtraction = (apiTicket.answers || []).map(answer => ({
    questionId: answer.questionId,
    question: {
      questionTranslationKey: answer.question?.questionTranslationKey || '',
    },
    answer: answer.answer || '',
  }));

  const employeeType =
    inBehalfOfId && inBehalfOf ? 'onBehalfOf' : 'requestedBy';
  const detailsFromAnswers = extractEmployeeDetailsFromAnswers(
    answersForExtraction,
    employeeType
  );

  const description = generateDescription(apiTicket, formType, category);
  const dynamicHoverData = detectAndMapDynamicHoverData({
    answers: apiTicket.answers || [],
    description,
    employee: {
      employeeId:
        detailsFromAnswers.globalId || employeeId || employeeGlobalId || 'N/A',
      band: detailsFromAnswers.band || employeeBand || 'Unknown',
      positionTitle: detailsFromAnswers.positionTitle || position || 'Unknown',
    },
    inBehalfOfId,
  });

  function kebabToCamelCase(str: string): string {
    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }

  const titleTranslationKey =
    apiFormType.translationKey || 'form.unknown.title';
  const formTypeCamelCase = apiFormType.name
    ? kebabToCamelCase(apiFormType.name)
    : 'unknown';
  const categoryTranslationKey =
    category && formType !== 'unknown'
      ? `form.${formTypeCamelCase}.${category}`
      : undefined;
  const subcategoryTranslationKey =
    subcategory &&
    subcategory !== 'defaultSubcategory' &&
    categoryTranslationKey
      ? `${categoryTranslationKey}.${subcategory}`
      : undefined;

  const name =
    detailsFromAnswers.name ||
    (inBehalfOfId && inBehalfOf ? inBehalfOf.name : requestedByName);

  return {
    id,
    title: titleTranslationKey,
    status,
    ticketNumber: numericId,
    description,
    businessUnit: businessUnit || 'Unknown',
    createDate: formatApiDateToUI(createdAt),
    zone: detailsFromAnswers.zone || zone?.name || 'Unknown',
    employee: {
      name,
      employeeId:
        detailsFromAnswers.globalId || employeeId || employeeGlobalId || 'N/A',
      band: detailsFromAnswers.band || employeeBand || 'Unknown',
      managerName:
        detailsFromAnswers.managerName || managerLevel1Name || 'Unknown',
      positionTitle: detailsFromAnswers.positionTitle || position || 'Unknown',
    },
    formType,
    category: categoryTranslationKey,
    subcategory: subcategoryTranslationKey,
    specificData,
    dynamicHoverData: dynamicHoverData || undefined,
    answers: apiTicket.answers,
  };
}

export function apiTicketsToUITickets(apiTickets: ApiTicket[]): Ticket[] {
  return apiTickets.map(apiTicketToUITicket);
}
